// Protected Admin Route - Only allows admin users
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@stores/useAuthStore';
import { supabase } from '@config/supabase';
import { Card } from '@components/ui';

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
}

export const ProtectedAdminRoute: React.FC<ProtectedAdminRouteProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuthStore();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAdminAccess();
  }, [user, isAuthenticated]);

  const checkAdminAccess = async () => {
    if (!isAuthenticated || !user) {
      setIsAdmin(false);
      setIsLoading(false);
      return;
    }

    try {
      // Check if user has admin flag in profile
      const { data, error } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Admin check error:', error);
        setIsAdmin(false);
      } else {
        setIsAdmin(data?.is_admin === true);
      }
    } catch (error) {
      console.error('Failed to check admin access:', error);
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-100 flex items-center justify-center">
        <Card className="p-8">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
            <p className="text-white/60 font-inter">Checking admin access...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-dark-100 flex items-center justify-center p-4">
        <Card className="p-8 max-w-md">
          <div className="text-center">
            <h2 className="font-dela text-2xl text-white mb-4">Access Denied</h2>
            <p className="text-white/60 font-inter mb-6">
              You don't have permission to access this page.
            </p>
            <a
              href="/"
              className="inline-block px-6 py-3 bg-primary text-black font-inter font-bold rounded-lg hover:bg-primary/90 transition-all"
            >
              Go to Dashboard
            </a>
          </div>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};



